import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updatePasswordApis } from '../redux/users/userActions';
import { selectUpdatePasswordStatus } from '../redux/users/userSelectors';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';
import '../assets/css/setPassword.css';

const SetPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Get email from URL
  const queryParams = new URLSearchParams(location.search);
  const emailFromUrl = queryParams.get('email');

  // Redux state
  const updateStatus = useSelector(selectUpdatePasswordStatus);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ---------------- VALIDATION LOGIC ----------------
  const hasLength = password.length >= 5;
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isMatch = password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;

  const isValid = hasLength && hasNumber && hasSpecial && isMatch;

  // Helper for Validation UI Colors
  const getReqClass = (met) => {
    if (password.length === 0) return 'text-muted'; 
    return met ? 'text-success' : 'text-danger';
  };

  // ---------------- SUBMIT ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailFromUrl) {
      toast.error('Email not found in URL.');
      return;
    }
    if (!isValid) {
      toast.error('Please satisfy all password requirements.');
      return;
    }

    const payload = { email: emailFromUrl, newPassword: password };
    const result = await dispatch(updatePasswordApis(payload));

    if (result.meta.requestStatus === 'fulfilled') {
      toast.success('Password reset successfully!');
      navigate('/login');
    }
  };

  return (
    <div className="password-page-wrapper">
      {updateStatus === 'loading' && <Loader isLoading={true} />}

      <div className="purple-header-bar">
        <h2 className="header-title">Reset Password</h2>
      </div>

      <div className="white-content-area">
        <div className="content-container">
          <h3 className="fw-bold mb-3 text-center text-dark">Enter New Password</h3>

          <p className="text-muted text-center mb-4 small-text">
            Updating password for:{' '}
            <span className="text-primary fw-bold">{emailFromUrl || 'Unknown Email'}</span>
          </p>

          <form onSubmit={handleSubmit}>
            {/* NEW PASSWORD */}
            <div className="form-group mb-3 text-start">
              <label className="form-label fw-bold">Password</label>
              <div className="custom-input-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="custom-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value.replace(/\s/g, ''))}
                />
                <i
                  className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'} password-toggle-icon`}
                  onClick={() => setShowPassword(!showPassword)}
                />
              </div>

              {/* Requirement Checklist UI */}
              <div className="validation-box mt-3">
                <div className={`req-item ${getReqClass(hasLength)}`}>
                  <i className={`bi ${hasLength ? 'bi-check-circle-fill' : 'bi-circle'} me-2`}></i>
                  Min 5 characters
                </div>
                <div className={`req-item ${getReqClass(hasNumber)}`}>
                  <i className={`bi ${hasNumber ? 'bi-check-circle-fill' : 'bi-circle'} me-2`}></i>
                  1 number
                </div>
                <div className={`req-item ${getReqClass(hasSpecial)}`}>
                  <i className={`bi ${hasSpecial ? 'bi-check-circle-fill' : 'bi-circle'} me-2`}></i>
                  1 special character
                </div>
              </div>
            </div>

            {/* CONFIRM PASSWORD */}
            <div className="form-group mb-4 text-start">
              <label className="form-label fw-bold">Confirm Password</label>
              <div className="custom-input-group">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`custom-input ${confirmPassword && !isMatch ? 'border-danger' : ''}`}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value.replace(/\s/g, ''))}
                />
                <i
                  className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'} password-toggle-icon`}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                />
              </div>

              {confirmPassword && (
                <div className={`mt-2 small fw-bold ${isMatch ? 'text-success' : 'text-danger'}`}>
                  {isMatch ? '✓ Passwords match' : '✕ Passwords do not match'}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="btn-primary-custom-model w-100"
              disabled={!isValid || updateStatus === 'loading'}
            >
              {updateStatus === 'loading' ? 'Updating...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;